<template>
  <form class="bulk-sites-form" @submit.prevent="handleSubmit">
    <p class="bulk-sites-form__intro">{{ $t('customer_accounts.sites.bulk.intro') }}</p>

    <ul class="bulk-sites-form__examples">
      <li><code>Dorpsstraat 12, 1234 AB Utrecht</code></li>
      <li><code>Blok A: Dorpsstraat 14, 1234 AB Utrecht</code></li>
      <li><code>Blok B ⇥ Dorpsstraat ⇥ 16 ⇥ 1234 AB ⇥ Utrecht</code></li>
    </ul>

    <TextAreaInput
      id="bulk_sites_lines"
      :label="$t('customer_accounts.sites.bulk.lines')"
      v-model="text"
      :placeholder="$t('customer_accounts.sites.bulk.placeholder')"
      :rows="10"
    />

    <div v-if="result?.failed?.length" class="bulk-sites-form__failed">
      <strong>
        {{ $t('customer_accounts.sites.bulk.failed_title', { count: result.failed.length }) }}
      </strong>
      <ul>
        <li v-for="(item, index) in result.failed" :key="index">
          <code>{{ item.line }}</code>
        </li>
      </ul>
      <span>{{ $t('customer_accounts.sites.bulk.failed_hint') }}</span>
    </div>

    <div class="bulk-sites-form__actions">
      <span class="bulk-sites-form__count">
        {{ $t('customer_accounts.sites.bulk.count', { count: lineCount }) }}
      </span>
      <Btn type="button" variant="outlined" @click="onCancel && onCancel()">
        {{ $t('common.close') }}
      </Btn>
      <Btn type="submit" variant="primary" :loading="saving" :disabled="!lineCount">
        {{ $t('customer_accounts.sites.bulk.submit', { count: lineCount }) }}
      </Btn>
    </div>
  </form>
</template>

<script>
  import { defineComponent, ref, computed } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import { countBulkLines } from '@shared/utils/customerAccounts';

  export default defineComponent({
    name: 'BulkAddSitesModal',
    components: { Btn, TextAreaInput },
    props: {
      // Resolves to { created, failed }; failed lines stay in the textarea for correction.
      onSubmit: { type: Function, required: true },
      onCancel: { type: Function, default: null },
    },
    setup(props) {
      const text = ref('');
      const saving = ref(false);
      const result = ref(null);

      const lineCount = computed(() => countBulkLines(text.value));

      const handleSubmit = async () => {
        const lines = text.value.split(/\r?\n/).filter((line) => line.trim());
        if (!lines.length) return;
        saving.value = true;
        try {
          result.value = await props.onSubmit(lines);
          if (result.value?.failed?.length) {
            text.value = result.value.failed.map((item) => item.line).join('\n');
          }
        } finally {
          saving.value = false;
        }
      };

      return { text, saving, result, lineCount, handleSubmit };
    },
  });
</script>

<style scoped lang="scss">
  .bulk-sites-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__intro {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__examples {
      margin: 0;
      padding: 0.75rem 1rem;
      list-style: none;
      background: var(--slate-50);
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      code {
        font-size: 0.8125rem;
        color: var(--slate-700);
      }
    }

    &__failed {
      padding: 0.75rem 1rem;
      background: var(--amber-50);
      border: 1px solid var(--amber-200);
      border-radius: 0.5rem;
      font-size: 0.8125rem;
      color: var(--amber-800);

      ul {
        margin: 0.5rem 0;
        padding-left: 1rem;
      }
    }

    &__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-200);
    }

    &__count {
      margin-right: auto;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }
  }
</style>
